import { Injectable } from '@angular/core';
import { ItemDetail } from 'app/model/entities/item-detail';
import { ItemDetailAddon } from 'app/model/entities/item-detail-addon';
import { JoDetail } from 'app/model/entities/jo-detail';

import { Addon } from 'app/model/entities/addon';
import { AddonBin } from 'app/model/entities/addon-bin';
import { AddonStation } from 'app/model/entities/addon-station';
import { AddonBinStationStatusEnum, AddonBinStatusEnum, ItemDetailStatusEnum } from 'app/model/entities/enums';
import { JoHeader } from 'app/model/entities/jo-header';
import { EntityState } from 'breeze-client';
import _ from 'lodash';
import { AuthService } from './auth.service';
import { BusyService } from './busy.service';
import { DbQueryService } from './db-query.service';
import { DbSaveService } from './db-save.service';
import { DialogWrapperService } from './dialog-wrapper.service';
import { UnitOfWork } from './unit-of-work';
import { UtilFns } from './util-fns';


@Injectable({providedIn: 'root'})
export class AddonUtilsService {
  constructor(
    public uow: UnitOfWork,
    public dbQueryService: DbQueryService,
    public dbSaveService: DbSaveService,
    public dialogWrapperService: DialogWrapperService,
    public authService: AuthService,
    public busyService: BusyService
  ) {}



  // assumes that both JoDetailAddons and ItemDetailAddons have been queried and Product for both Jod and Itd.
  createItemDetailAddonsFromJoDetail(jod: JoDetail, itd: ItemDetail, accountAddons: Addon[]) {
    const now = new Date(Date.now());
    const user = this.authService.getUser();
    const itdAddons = itd.itemDetailAddons.slice();
    // fn to create a copy of the just the specified fields from another object.

    const newItdAddons: ItemDetailAddon[] = [];
    jod.joDetailAddons.forEach(joda => {
      // if the same addon already exists on the itemDetail do NOT add another
      if (!itdAddons.some(itda => itda.addonId == joda.addonId)) {
        const itdaStruct = <ItemDetailAddon> { addonId: joda.addonId, additionalInfo: joda.additionalInfo };
        itdaStruct.itemDetailId = itd.id;
        itdaStruct.modTs = now;
        itdaStruct.modUserInit = user.initials;
        const itda = this.uow.createEntity(ItemDetailAddon, itdaStruct, EntityState.Added);
        newItdAddons.push(itda);
      }
    });
    // if product ids don't match - create a new addon
    if (jod.product.id != itd.product.id) {
      const itda = this.createMismatchAddon(jod, itd, accountAddons);
      newItdAddons.push(itda)
    }

    return newItdAddons;
  }

  private createMismatchAddon(jod: JoDetail, itd: ItemDetail, accountAddons: Addon[]) {
    const now = new Date(Date.now());
    const user = this.authService.getUser();
    const mismatchFeature = _.first(jod.product.productFeatureChoices).featureChoice.feature;
    if (mismatchFeature.mismatchAddonNameAndLocation == null) {
      // will require a saveChanges call
      mismatchFeature.mismatchAddonNameAndLocation = "Feature Mismatch";
    }
    const mismatchName = mismatchFeature.mismatchAddonNameAndLocation;
    var mismatchAddon = accountAddons.find(x => x.nameAndLocation == mismatchName);
    if (mismatchAddon == null) {
      // TODO: consider whether to make use of mismatchAddonIsCrossAccount or not.
      mismatchAddon = this.uow.createEntity(Addon, {
        accountId: jod.joHeader.accountId,
        nameAndLocation: mismatchName,
        groupingKey: Math.random().toString(36).substring(0, 4),
        addonStationId: AddonStation.TailoringStationId,
        isHiddenOnDocs: true,
        // isCrossAccountAddon: mismatchFeature.mismatchAddonIsCrossAccount,
        needsAdditionalInfo: true,
      });
      accountAddons.push(mismatchAddon);
    }
    const itdaStruct = <ItemDetailAddon>{
      itemDetailId: itd.id,
      addonId: mismatchAddon.id,
      additionalInfo: `from: '${itd.product.getFeatureChoicesExtract()}' to: '${jod.product.getFeatureChoicesExtract()}'`,
      modTs: now,
      modUserInit: user.initials
    };
    const itda = this.uow.createEntity(ItemDetailAddon, itdaStruct, EntityState.Added);
    return itda;
  }

  buildAggregateGroupingKey(addons: Addon[]) {
    return _.uniq(addons.map(x => + x.addonStationId.toString() + '^' + x.groupingKey )).sort().join(':');
  }

  async findOrCreateAddonBins(itemDetails: ItemDetail[]) {
    // insure that these run sequentially - we don't want them to run
    // in parallel because each iter may find an AddonBin created
    // by an earlier iter.
    var addonBins = new Set<AddonBin>();
    for (const itd of itemDetails){
      const addonBin = await this.findOrCreateAddonBin(itd);
      if (addonBin != null) {
        addonBins.add(addonBin);
      }
    }
    // returns a uniq list
    return Array.from(addonBins);
  }

  /** get add-on bid from the entity cache for the given joHeader and aggGroupingKey */
  private getAddonBinByKey(joHeader: JoHeader, aggGroupingKey: string) {
    // query locally created or cached bins
    let addonBins = this.dbQueryService.getAlreadyFetched(AddonBin).filter(
      aob => aob.accountId == joHeader.accountId && aob.aggregateGroupingKey == aggGroupingKey);
    // only bins that have less then the max batch size in terms of number of items ( not itemDetailAddons)
    addonBins = addonBins.filter(x => _.uniq(x.itemDetailAddons.map(x => x.itemDetailId)).length < this.dbQueryService.appConfig.addon.maxBatchSize);
    var addonBin = _.first(addonBins.filter(b => (b.joHeaderId == null) || joHeader.id == b.joHeaderId ));
    return addonBin;
  }

  // assumes that itemDetail.joDetail.joHeader already fetched
  // assumes that itemDetail.itemDetailAddons.addon.addonStation already fetched
  private async findOrCreateAddonBin(itemDetail: ItemDetail) {
    // stock item
    if (itemDetail.joDetail == null) {
      return null;
    }
    const joHeader = itemDetail.joDetail.joHeader;

    const itdas = itemDetail.itemDetailAddons;
    if (itdas.length == 0) {
      return null;
    }
    const addonsFromItd = itdas.map(x => x.addon);
    const aggGroupingKey = this.buildAggregateGroupingKey(addonsFromItd);
    let addonBin = this.getAddonBinByKey(joHeader, aggGroupingKey);
    if (!addonBin) {
      // if we didn't find one locally, load more bins from db and retry
      await this.dbQueryService.getAvailableAddonBinsFor(joHeader.accountId, aggGroupingKey);
      UtilFns.wait(1); // wait for entities to settle into cache
      addonBin = this.getAddonBinByKey(joHeader, aggGroupingKey);
    }

    if (addonBin == null) {
      addonBin = this.dbSaveService.createEntity(AddonBin, {
        accountId: itemDetail.joDetail.joHeader.accountId,
        aggregateGroupingKey: aggGroupingKey,
        addonBinStatusId: AddonBinStatusEnum.New,
        priorityOrder: 9999,
      });
    }

    // when adding items to an addonBin - the binStation must go back to pending
    if (addonBin.currentAddonBinStationStatusId != null) {
      addonBin.pendingAddonStationId = addonBin.currentAddonStationId;
      addonBin.currentAddonStationId = null;
      addonBin.currentAddonBinStationStatus = null;
    }

    itdas.forEach(ida => {
      ida.addonBinId = addonBin.id;
      ida.addonBinStationStatusId = AddonBinStationStatusEnum.Pending;
    });

    this.updatePendingAddonStation(addonBin);


    return addonBin;
  }


  /**
   * A bin reopens when new items are added to it (see findOrCreateAddonBin), which leaves
   * already-Closed rows sitting at the same station as the newly Pending ones. Matching on
   * station alone therefore re-scans finished work, so a Closed row is never scannable.
   */
  public isAddonRowScannable(itda: ItemDetailAddon, addonBin: AddonBin) {
    if (itda?.addon == null || addonBin?.pendingAddonStationId == null) return false;
    return itda.addon.addonStationId == addonBin.pendingAddonStationId
      && itda.addonBinStationStatusId != AddonBinStationStatusEnum.Closed;
  }

  /** Scanned on this pass, or already finished on an earlier pass through the same station. */
  public isAddonRowSettled(itda: ItemDetailAddon) {
    return itda.addonBinStationStatusId == AddonBinStationStatusEnum.Await
      || itda.addonBinStationStatusId == AddonBinStationStatusEnum.Closed;
  }

  public findScannableAddons(addonBin: AddonBin, itemDetailAddons: ItemDetailAddon[], itemDetailId: string) {
    return itemDetailAddons.filter(x => x.itemDetailId == itemDetailId && this.isAddonRowScannable(x, addonBin));
  }

  /**
   * Marks itdas scanned into the bin's pending station and rolls the bin forward when nothing
   * is left to scan there. Returns true if the station rolled forward.
   */
  public applyScanIntoPendingStation(addonBin: AddonBin, itemDetailAddons: ItemDetailAddon[], itdas: ItemDetailAddon[]) {
    itdas.forEach(x => x.addonBinStationStatusId = AddonBinStationStatusEnum.Await);

    new Set(itdas.map(x => x.itemDetail)).forEach(itd => {
      // Same guard as updatePendingAddonStation: never pull an item that has already reached
      // Ship Prep, a voucher or a shipment back to the addon stage.
      if (itd.itemDetailStatusId >= ItemDetailStatusEnum.InProcessShipPrep) return;
      if (itd.invoiceHeaderId != null) return;
      itd.itemDetailStatusId = ItemDetailStatusEnum.InProcessAddon;
    });

    // Closed rows count as settled, otherwise a reopened bin can never roll forward.
    const isPendingStationAllScanned = itemDetailAddons
      .filter(x => x.addon?.addonStationId == addonBin.pendingAddonStationId)
      .every(x => this.isAddonRowSettled(x));

    if (isPendingStationAllScanned) {
      addonBin.currentAddonStationId = addonBin.pendingAddonStationId;
      addonBin.pendingAddonStationId = null;
      addonBin.currentAddonBinStationStatusId = AddonBinStationStatusEnum.Await;
    }
    return isPendingStationAllScanned;
  }

  // called after AddonBin and ItemDetailAddons have been updated for completion.
  public updatePendingAddonStation(addonBin: AddonBin) {
    const areAllClosed = addonBin.itemDetailAddons.every(x => x.addonBinStationStatusId == AddonBinStationStatusEnum.Closed);
    if (areAllClosed) {
      addonBin.addonBinStatusId = AddonBinStatusEnum.Closed;
    }

    // calculate the pending station - it may have changed as a result of adding addons.
    var openStations = addonBin.itemDetailAddons
      .filter(x => x.addonBinStationStatusId != AddonBinStationStatusEnum.Closed)
      .map(x => x.addon.addonStation)
      .filter(x => !!x);
    // remove dups
    openStations = Array.from(new Set(openStations));

    if (openStations.length > 0) {
      if (addonBin.pendingAddonStationId == null || openStations.every(x => x.id != addonBin.pendingAddonStationId)) {
        // set pendingAddonStation either because its null or because the current pending is no longer available
        const pendingAddonStation = _.first(_.sortBy(openStations, x => x.defaultPriorityOrder));
        addonBin.pendingAddonStationId = pendingAddonStation.id;
      }
    } else {
      addonBin.pendingAddonStationId = null
      addonBin.currentAddonBinStationStatusId = AddonBinStationStatusEnum.Closed;
      addonBin.itemDetailAddons.forEach(x => x.addonBinStationStatusId = AddonBinStationStatusEnum.Closed);
      var itds = Array.from(new Set(addonBin.itemDetailAddons.map(x => x.itemDetail)));
      itds.forEach(x => {
        // Guard against regressing items that have already left the addon stage.
        // If the bin is being (re-)closed after items have moved to Ship Prep, been
        // vouchered, or shipped, do NOT rewrite their status back to Pending Ship
        // Prep — that produced the "voucher-status misalignment" bug where items
        // ended up assigned to an invoice but showing as Ship Prep on the WIP grid.
        if (x.itemDetailStatusId >= ItemDetailStatusEnum.InProcessShipPrep) return;
        if (x.invoiceHeaderId != null) return;

        // joDetailId is set when an item has been pulled for an order (regular or account-owned)
        // and should continue through the shipping pipeline. When null, the item was received
        // from a PO into account-owned inventory (accountPurchaseJoDetailId is set instead)
        // and should return to inventory to await vouchering via "Create Account-Owned Vouchers".
        if (x.joDetailId != null) {
          x.itemDetailStatusId = ItemDetailStatusEnum.InProcessPendingShipPrep;
        } else {
          x.itemDetailStatusId = ItemDetailStatusEnum.InInventory;
        }
      });
    }

  }

  public async checkAndRemoveEmptyBins(addonBinIds: number[]) {
    const addonBins = await this.dbQueryService.getAddonBinsByIds(addonBinIds);

    addonBins.forEach(b => {
      if (b.itemDetailAddons.length == 0) {
        b.entityAspect.setDeleted();
      }
    });
  }
}
